<template>
  <div class="inline-image-editor" :class="{ 'inline-image-editor--disabled': disabled || isBusy }">
    <div v-if="!isPinturaReady" class="inline-image-editor__loading">Loading editor…</div>
    <component v-else :is="PinturaEditorAsync" ref="pinturaEditorRef" v-bind="mergedEditorProps" :src="src" :stickers="stickers" @pintura:process="onPinturaProcess" @pintura:close="onPinturaClose" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent, shallowRef, onMounted } from "vue";
import type { PinturaEditorDefaultOptions, PinturaDefaultImageWriterResult } from "@pqina/pintura";

type MarkupEditorToolbar = ReturnType<typeof import("@pqina/pintura").createMarkupEditorToolbar>;
type MarkupEditorToolStyles = ReturnType<typeof import("@pqina/pintura").createMarkupEditorToolStyles>;

// Note: They have really good documentation on how to customize the editor.
// https://pqina.nl/pintura/
// NOTE! Dynamically importing this since it's fairly large and not used often.
const PinturaEditorAsync = defineAsyncComponent(() => Promise.all([import("@pqina/pintura/pintura.css"), import("@pqina/vue-pintura")]).then(([, mod]) => mod.PinturaEditor));

const props = withDefaults(
  defineProps<{
    src: string;
    editorOptions?: Partial<PinturaEditorDefaultOptions>;
    disabled?: boolean;
    isBusy?: boolean;
  }>(),
  {
    editorOptions: () => ({}),
    disabled: false,
    isBusy: false,
  }
);

const emit = defineEmits<{
  (e: "process", payload: { dest: Blob | File }): void;
  (e: "close"): void;
  (e: "error", err: unknown): void;
}>();

const baseDefaults = shallowRef<Partial<PinturaEditorDefaultOptions>>({});
const markupEditorToolbar = shallowRef<MarkupEditorToolbar | null>(null);
const markupEditorToolStyles = shallowRef<MarkupEditorToolStyles | null>(null);
const imageWriter = shallowRef<ReturnType<typeof import("@pqina/pintura").createDefaultImageWriter> | null>(null);
const isPinturaReady = ref(false);

onMounted(async () => {
  const { getEditorDefaults, createMarkupEditorToolbar, createDefaultColorOptions, createMarkupEditorToolStyles, createDefaultImageWriter } = await import("@pqina/pintura");
  baseDefaults.value = getEditorDefaults();
  // CUSTOMIZE: Which image format to save
  imageWriter.value = createDefaultImageWriter({
    mimeType: "image/jpeg",
    quality: 0.8,
  });

  // CUSTOMIZE: Reorders the Annotate tool order
  markupEditorToolbar.value = createMarkupEditorToolbar(["text", "line", "arrow", "rectangle", "ellipse", "preset"]);
  const colors = createDefaultColorOptions();

  // CUSTOMIZE: Text tool styles
  markupEditorToolStyles.value = createMarkupEditorToolStyles({
    text: {
      fontSize: "10%",
      color: colors.black,
      // backgroundColor: colors.transparent,
    },
    /* rectangle: {
      backgroundColor: colors.yellow,
    },*/
  });
  isPinturaReady.value = true;
});

const cropAspectRatioPresets: Array<[number | undefined, string]> = [
  [undefined, "Custom"],
  [4 / 5, "4:5"],
  [1, "1:1 square"],
  // Landscape
  [1.91 / 1, "1.91:1 Instagram landscape"],
  [16 / 9, "16:9 Facebook landscape"],
  [4 / 3, "4:3"],
  // Portrait
  [3 / 4, "3:4"],
  [9 / 16, "9:16"],
];

const mergedEditorProps = computed(() => ({
  ...baseDefaults.value,
  ...props.editorOptions,
  // Hide Pintura's "Done" (export) control; parents finish via `processImage()` or their own UI.
  enableButtonExport: false,

  // CUSTOMIZE: Which tools to support
  utils: ["crop", "finetune", "filter", "annotate", "sticker"],
  // CUSTOMIZE: Crop/Aspect ratio button and options
  cropSelectPresetOptions: cropAspectRatioPresets,
  cropEnableSelectPreset: true,
  cropEnableButtonFlipVertical: true,
  cropSelectPresetFilter: "portrait",
  ...(markupEditorToolbar.value ? { markupEditorToolbar: markupEditorToolbar.value } : {}),
  ...(markupEditorToolStyles.value ? { markupEditorToolStyles: markupEditorToolStyles.value } : {}),
  ...(imageWriter.value ? { imageWriter: imageWriter.value } : {}),
}));

// CUSTOMIZE: Which stickers to show
const stickers = ["🎉", "👍", "📍", "❤️", "☀️", "🍂", "❄️", "🌸", "🌲", "🌊", "🎶", "🍽️", "🍔", "🍕", "🍺", "☕", "🍷", "⛰️", "🏖️", "🔥", "🌈", "🌙"];

const pinturaEditorRef = ref<{ editor?: { processImage: () => Promise<PinturaDefaultImageWriterResult> } } | null>(null);

function onPinturaProcess(event: CustomEvent<{ dest: Blob | File }>) {
  try {
    emit("process", { dest: event.detail.dest });
  } catch (err) {
    emit("error", err);
  }
}

function onPinturaClose() {
  emit("close");
}

async function processImage(): Promise<PinturaDefaultImageWriterResult | null> {
  const inst = pinturaEditorRef.value as { editor?: { processImage: () => Promise<PinturaDefaultImageWriterResult> } } | null;
  if (!inst?.editor) return null;
  return await inst.editor.processImage();
}

defineExpose({ processImage });
</script>

<style scoped lang="scss">
.inline-image-editor {
  min-height: 400px;
  height: 70vh;

  &__loading {
    padding: 24px;
    text-align: center;
    color: rgba(0, 0, 0, 0.55);
  }

  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}
</style>
